import { Button, Input, Select, Table } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { FieldVisit } from '../../../../features/fieldVisit/fieldVisitSlice';
import {
  getOpenFieldVisitsThunk,
  getVisitedFieldVisitsThunk,
} from '../../../../services/fieldVisitService/fieldVisitThunk';
import { AppDispatch, RootState } from '../../../../store/store';
import { displayDate } from '../../../../utils/datetime.util';

import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { ColumnsType } from 'antd/es/table';
import AccountEnquiryModal from '../../../AccountEnquiry/AccountEnquiryModal';
import { FIELD_VISIT_STATUS } from '../../../../enum/fieldVisitStatus';

const { Search } = Input;

const VisitedTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { visitedFieldVisitListingState } = useSelector((state: RootState) => state.fieldVisit);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: pageNum ? +pageNum : 1,
        pageSize: pageSize ? +pageSize : 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: { status: 'VISITED' },
    }),
    [pageNum, pageSize]
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<FieldVisit>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('agreementNo');

  const tablePagination = {
    total: visitedFieldVisitListingState?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No.',
      dataIndex: 'index',
      render: (_: string, __, index: number) => index + 1,
    },
    {
      title: 'Field Visit No.',
      dataIndex: 'fieldVisitNo',
      key: 'fieldVisitNo',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/field-visit/details/${record.id}`)}>
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Field Visit Date',
      dataIndex: 'dateVisited',
      key: 'dateVisited',
      sorter: (a, b) => a.createdAt - b.createdAt,
      sortOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Agreement No',
      dataIndex: 'agreementNo',
      key: 'agreementNo',
      render: (_, record) => (
        <AccountEnquiryModal key={record.id} agreementNo={record.accountDetails.agreementNo} />
      ),
    },
    {
      title: 'Visit By',
      render: (_, record) =>
        record?.fieldVisitOfficerId === null
          ? `-`
          : `${record?.fvOfficer?.firstName} ${record?.fvOfficer?.lastName}`,
    },
  ];

  const fetchVisitedFieldVisit = async (params?: any) => {
    const parameters = {
      params: { ...params, filters: { status: FIELD_VISIT_STATUS.VISITED } },
    };
    await dispatch(getVisitedFieldVisitsThunk(parameters));
  };

  const onSearch = (value: string) => {
    value = value.trim();
    if (value) {
      fetchVisitedFieldVisit({
        ...initialParams,
        filters: { ...filterState },
        search: { [searchFiled]: value },
      });
      // setFilterState({ ...initialParams.filters, [searchFiled]: value });
    } else {
      fetchVisitedFieldVisit({
        ...initialParams,
        filters: { ...filterState },
      });
    }
  };

  const handleTableChange: TableProps<FieldVisit>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    setSortedInfo(sorter as SorterResult<FieldVisit>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });

    const param =
      sorter?.order == null
        ? {
            ...initialParams,
            ...filterState,
            pagination,
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          };

    fetchVisitedFieldVisit(param);

    queryParams.set('pageNum', `${pagination.current ?? 1}`);
    queryParams.set('pageSize', `${pagination.pageSize ?? 10}`);
    const newSearch = queryParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  return (
    <>
      <Helmet>
        <title>Visited Field Visit - redCASH CEP</title>
      </Helmet>
      <Input.Group compact>
        <Select
          defaultValue="agreementNo"
          style={{ width: 200 }}
          onChange={(value) => setSearchFiled(value)}
          options={[
            { value: 'applicationNo', label: 'Application No.' },
            { value: 'agreementNo', label: 'Agreement No.' },
            { value: 'applicantFirstName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' },
            { value: 'fieldVisitNo', label: 'Field Visit No.' },
            { value: 'visitBy', label: 'Field Recovery Officer' },
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={visitedFieldVisitListingState?.data ?? []}
        loading={visitedFieldVisitListingState?.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default VisitedTab;
